import React, { useState, useEffect, useRef } from 'react';
import '../../App.css';
import { useSpring, animated } from '@react-spring/web';
import { useAuth } from '../../contexts/AuthContext';
import vegi from '../../img/vegi.png';
import halal from '../../img/halal.png';
import { MinusOutlined, PlusOutlined, CloseOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useTranslation } from "../../i18n/useTranslation";

function ProductModal({
  setIsOpen,
  id,
  name,
  price,
  description,
  photos = [],
  translatedOpt,
  allergens,
  label,
  quantity: availableQuantity = 0,
}) {
  const { language, addToBasket, currentRestaurant, basket } = useAuth();
  const { t } = useTranslation();

  const photo = photos[0] || null;
  const [photoIndex, setPhotoIndex] = useState(0);
  const touchStartX = useRef(null);

  const imageDisplay = currentRestaurant?.imageDisplay
    || (currentRestaurant?.productType === 'digital' ? 'contain' : 'cover');
  const isContain = imageDisplay === 'contain';

  const [quantity, setQuantity] = useState(1);
  const [stockError, setStockError] = useState("");

  const [selectedMulti, setSelectedMulti] = useState({});
  const [selectedRadio, setSelectedRadio] = useState(null);
  const isDigitalProduct = currentRestaurant?.productType === "digital";

const [showFullDescription, setShowFullDescription] = useState(false);

const DESCRIPTION_LIMIT = 250;

const isFrench = !language || language.toLowerCase().startsWith("fr");

const shouldTruncateDescription =
  isFrench && description && description.length > DESCRIPTION_LIMIT;

const displayedDescription =
  shouldTruncateDescription && !showFullDescription
    ? `${description.slice(0, DESCRIPTION_LIMIT)}...`
    : description;

  const springs = useSpring({
    from: { y: 0 },
    to: { y: 0 },
  });

  const canOrder =
    currentRestaurant?.paymentEnabled === true &&
    currentRestaurant?.deliveryEnabled === true;

  const showInputs = canOrder;

  const getOptKey = (item, index) =>
    item?.id ?? item?._id ?? item?.optionId ?? `${item?.name}-${index}`;

  const toggleCheckbox = (key) => {
    if (!showInputs) return;
    setSelectedMulti((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const selectRadio = (key) => {
    if (!showInputs) return;
    setSelectedRadio(key);
  };

  const incQty = (e) => {
    e.stopPropagation();

    if (quantity >= Number(availableQuantity || 0)) {
      setStockError(`Stock insuffisant. Disponible : ${Number(availableQuantity || 0)}`);
      return;
    }

    setStockError("");
    setQuantity((q) => q + 1);
  };

  const decQty = (e) => {
    e.stopPropagation();
    setStockError("");
    setQuantity((q) => (q > 1 ? q - 1 : 1));
  };

  useEffect(() => {
    if (!translatedOpt || translatedOpt.length === 0) return;
    if (selectedRadio) return;

    const firstIndex = translatedOpt.findIndex((it) => it.type === "1");
    if (firstIndex === -1) return;

    setSelectedRadio(getOptKey(translatedOpt[firstIndex], firstIndex));
  }, [translatedOpt, selectedRadio]);

  useEffect(() => {
    if (Number(availableQuantity || 0) <= 0) {
      setQuantity(1);
      return;
    }

    if (quantity > Number(availableQuantity || 0)) {
      setQuantity(Number(availableQuantity || 0));
    }
  }, [availableQuantity, quantity]);

  const selectedOption =
    translatedOpt?.find((item, index) => {
      const key = getOptKey(item, index);
      return item.type === "1" && selectedRadio === key;
    }) || null;

  const selectedSupplements =
    translatedOpt?.filter((item, index) => {
      const key = getOptKey(item, index);
      return item.type === "2" && !!selectedMulti[key];
    }) || [];

  const basePrice = parseFloat(price) || 0;

  const supplementsTotal = selectedSupplements.reduce(
    (sum, it) => sum + (parseFloat(it.price) || 0),
    0
  );

  const unitPrice = selectedOption
    ? parseFloat(selectedOption.price) || 0
    : basePrice + supplementsTotal;

    const totalPrice = unitPrice * (isDigitalProduct ? 1 : quantity);

  let cartKey = `${id || name}__BASE`;

  if (selectedOption) {
    cartKey = `${id || name}__OPT__${selectedOption.id || selectedOption.name}`;
  } else if (selectedSupplements.length > 0) {
    const suppIds = selectedSupplements
      .map((s) => s?.id || s?.name)
      .filter(Boolean)
      .sort()
      .join(",");
    cartKey = `${id || name}__SUP__${suppIds}`;
  }

  const cartItem = {
    key: cartKey,
    restaurantId: currentRestaurant?.id,
    product: {
      id,
      name,
      description,
      photo,
      label,
      allergens,
      basePrice,
      quantity: Number(availableQuantity || 0),
    },
    quantity,
    selections: {
      option: selectedOption
        ? {
            id: selectedOption.id || null,
            name: selectedOption.name,
            price: selectedOption.price,
            type: selectedOption.type,
            translations: selectedOption.translations || null,
          }
        : null,
      supplements: selectedSupplements.map((item) => ({
        id: item.id || null,
        name: item.name,
        price: item.price,
        type: item.type,
        translations: item.translations || null,
      })),
    },
    pricing: {
      unitPrice,
      supplementsTotal,
      totalPrice,
      currency: "EUR",
    },
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
  
    if (!showInputs) return;
  
    if (Number(availableQuantity || 0) <= 0) {
      setStockError("");
      return;
    }
  
    if (isDigitalProduct) {
      const alreadyInBasket = basket?.some((item) => item?.product?.id === id);
  
      if (alreadyInBasket) {
        setStockError("");
        setIsOpen(false);
        return;
      }
    }
  
    if (!isDigitalProduct && quantity > Number(availableQuantity || 0)) {
      setStockError("");
      return;
    }
  
    const finalCartItem = {
      ...cartItem,
      quantity: isDigitalProduct ? 1 : quantity,
      pricing: {
        ...cartItem.pricing,
        totalPrice: unitPrice * (isDigitalProduct ? 1 : quantity),
      },
    };
  
    setStockError("");
    addToBasket(finalCartItem);
    setIsOpen(false);
  };

  const isOutOfStock = Number(availableQuantity || 0) <= 0;
  const isMaxReached = quantity >= Number(availableQuantity || 0) && !isOutOfStock;

  return (
    <div className="product-modal-container" onClick={() => setIsOpen(false)}>
  <animated.div
    className={`product-modal-content product-modal-layout modal-total product-modal-content--digital ${translatedOpt?.length > 0 && photos.length > 0 ? "modal-options" : ""}`}
    style={{ ...springs }}
    onClick={(e) => e.stopPropagation()}
  >
    <button
      type="button"
      className="product-modal-close"
      onClick={(e) => {
        e.stopPropagation();
        setIsOpen(false);
      }}
    >
      <CloseOutlined />
    </button>

    <div className="product-modal-top">
      {photos.length > 0 ? (
        <div
          className="product-modal-carousel"
          onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (touchStartX.current === null) return;
            const delta = e.changedTouches[0].clientX - touchStartX.current;
            if (delta < -50 && photoIndex < photos.length - 1) setPhotoIndex((i) => i + 1);
            if (delta > 50 && photoIndex > 0) setPhotoIndex((i) => i - 1);
            touchStartX.current = null;
          }}
        >
          <img
            className={`product-modal-img ${isContain ? "product-modal-img--digital" : ""}`}
            alt=""
            src={photos[photoIndex]}
          />
          {photos.length > 1 && (
            <>
              {photoIndex > 0 && (
                <button type="button" className="carousel-arrow carousel-arrow-left" onClick={(e) => { e.stopPropagation(); setPhotoIndex((i) => i - 1); }}><LeftOutlined /></button>
              )}
              {photoIndex < photos.length - 1 && (
                <button type="button" className="carousel-arrow carousel-arrow-right" onClick={(e) => { e.stopPropagation(); setPhotoIndex((i) => i + 1); }}><RightOutlined /></button>
              )}
              <div className="carousel-dots">
                {photos.map((_, i) => (
                  <span key={i} className={`carousel-dot ${i === photoIndex ? "carousel-dot-active" : ""}`} onClick={(e) => { e.stopPropagation(); setPhotoIndex(i); }} />
                ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <img className={`product-modal-img hide-img-modal ${isContain ? "product-modal-img--digital" : ""}`} alt="" src="" />
      )}
    </div>

    <div className="product-modal-scroll">
      <div className={`modal-text-container   ${photos.length === 0 && "plus-padding"}`}>
        <div className="modal-header-container">
          <p className={`modal-text-header ${translatedOpt?.length > 0 && translatedOpt[0]?.type === "1" && "modal-header-plus"}`}>
            {name}{" "}
            {label && (
              <span>
                <img
                  className="modal-label"
                  src={label === "halal" ? halal : vegi}
                  alt="Logo"
                />
              </span>
            )}
          </p>

          {translatedOpt?.length === 0 && (
            <p className="modal-text-price">{parseFloat(price).toFixed(2)} €</p>
          )}

          {!translatedOpt && (
            <p className="modal-text-price">{parseFloat(price).toFixed(2)} €</p>
          )}

          {translatedOpt?.length > 0 && translatedOpt[0]?.type === "2" && (
            <p className="modal-text-price">{parseFloat(price).toFixed(2)} €</p>
          )}

          {translatedOpt?.length > 0 && translatedOpt[0]?.type === "1" && (
            <p className="modal-text-price"></p>
          )}
        </div>

        {name !== description || (name === description && language) ? (
  <p className="modal-text-description">
    {displayedDescription}

    {shouldTruncateDescription && (
      <span
        className="modal-see-more"
        onClick={(e) => {
          e.stopPropagation();
          setShowFullDescription((prev) => !prev);
        }}
      >
        {showFullDescription ? "" : " Voir plus"}
      </span>
    )}
  </p>
) : null}
      </div>

      {allergens && (
        <div className="allergen modal-allergen">
          <p>{allergens}</p>
        </div>
      )}

      {translatedOpt && translatedOpt.length > 0 && (
        <div className="modal-text-container">
          {translatedOpt[0]?.type === "2" && (
            <p className="modal-text-sub-header">{t("suplements")}</p>
          )}

          {translatedOpt[0]?.type === "1" && (
            <p className="modal-text-sub-header">{t("options")}</p>
          )}

          <div className="modal-option">
            {translatedOpt.map((item, index) => {
              const key = getOptKey(item, index);
              const isRadio = item.type === "1";
              const checked = isRadio ? selectedRadio === key : !!selectedMulti[key];

              return (
                <div key={item.id || index} className="modal-text-option">
                  <div
                    className="modal-opt-left"
                    onClick={() => (showInputs ? (isRadio ? selectRadio(key) : toggleCheckbox(key)) : null)}
                  >
                    {showInputs && (
                      <input
                        type={isRadio ? "radio" : "checkbox"}
                        name={isRadio ? "modal-radio-group" : undefined}
                        className="modal-opt-checkbox"
                        checked={checked}
                        onChange={() => (isRadio ? selectRadio(key) : toggleCheckbox(key))}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}

                    <p className="modal-max-width">
                      {language
                        ? item.translations !== undefined
                          ? item.translations[language]
                          : item.name
                        : item.name}
                    </p>
                  </div>

                  <p className="modal-max-width">
                    {item.price < 0.1
                      ? ""
                      : `${item.type === "2" ? "+" : ""}${parseFloat(item.price).toFixed(2)} €`}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {stockError ? (
        <div className="modal-text-container">
          <p className="auth-error">{stockError}</p>
        </div>
      ) : null}
    </div>

    {showInputs && (
      <div className="modal-close-container product-modal-footer">
        {!isDigitalProduct && (
  <div className="modal-quantity-container">
    <div>{t("quantity")}</div>
    <div className="modal-quantity">
      <MinusOutlined onClick={decQty} />
      <p>{quantity}</p>
      <PlusOutlined
        onClick={incQty}
        style={{
          opacity: isOutOfStock || isMaxReached ? 0.4 : 1,
          pointerEvents: isOutOfStock || isMaxReached ? "none" : "auto",
        }}
      />
    </div>
  </div>
)}

        <div
          className="modal-close-button"
          onClick={handleAddToCart}
        >
          {isOutOfStock
            ? "Rupture de stock"
            : `${t("add_to_cart")} · ${totalPrice.toFixed(2)} €`}
        </div>
      </div>
    )}
  </animated.div>
</div>
  );
}

export default ProductModal;